/**
 * What the app is paying for in storage, how much of it is in use, and how
 * that moved.
 *
 * It sits on the PLAN screen, under the plan card, because that is the
 * question it answers: how much of a thing you bought are you using. On the
 * Database screen it was a billing block on top of a browser — the first
 * thing you met when you came to look at your rows.
 *
 * The quota is `Σ quantity × maxStorageBytes` over the app's ACTIVE database
 * extras — summed server-side (`resolveDatabaseQuota`), with the pack count
 * alongside it so the line under the bar can say what the number is made of.
 * An app with three packs reads "3 packs · 3.0 GB", never a bare total that
 * leaves the customer working out whether all three were counted.
 *
 * Two things here are sized for the common case rather than the impressive
 * one, because the first version got that backwards and the panel read as
 * broken:
 *
 *   - A new database uses ~1% of its quota. A 1%-wide pill is a dot, so the
 *     fill carries a minimum width and reads as a short bar.
 *   - The chart is scaled to the highest day it is drawing, NOT to the quota.
 *     Scaled to quota, every database that is not nearly full is a strip of
 *     empty box; scaled to its own peak, the curve is legible from the first
 *     week, which is what a daily series is for. How full the app is, is the
 *     bar's job, and it is right above.
 *
 * And it does not draw a chart of one point. One bar is not a trend, it is a
 * number that already appears at the top of the card.
 */
import { AppDatabaseUsage } from '@back4app2/sdk';
import { Eyebrow, SurfaceCard } from '../../components/ds';
import { formatBytes } from './cells';

/** The window the chart draws, and the window `appDatabaseUsage` is asked for. */
const DAYS = 30;

const TONE: Record<string, { bar: string, text: string, label: string }> = {
  OK: { bar: 'bg-state-ok', text: 'text-state-ok', label: 'Within quota' },
  WARN: { bar: 'bg-state-warn', text: 'text-state-warn', label: 'Filling up' },
  OVER: { bar: 'bg-state-err', text: 'text-state-err', label: 'Over quota' },
};

const dayLabel = (value: Date | string) =>
  new Date(value).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

const measuredLabel = (value: Date | string) => {
  const date = new Date(value);
  const hours = Math.floor((Date.now() - date.getTime()) / 3600000);

  if (hours < 1) {
    return 'measured minutes ago';
  }
  if (hours < 24) {
    return `measured ${hours}h ago`;
  }

  const days = Math.floor(hours / 24);

  return `measured ${days} day${days === 1 ? '' : 's'} ago`;
};

/**
 * What the series says, in one line.
 *
 * A quota display exists to answer "am I going to run out", and a bar at 1%
 * cannot answer it — every database that is not nearly full looks identical.
 * The daily points can: the distance between the first and the last is the
 * growth, and the growth against the headroom is the date.
 *
 * The projection is deliberately cautious about what it will claim:
 *
 *   - fewer than 7 measured days and it does not project at all. Two points
 *     is a line through two points, and a database that was seeded on the
 *     first day would read as "full next week";
 *   - flat or shrinking, it says so rather than dividing by a slope of zero;
 *   - beyond two years it stops counting and says "years", because a number
 *     of months that large is arithmetic, not a forecast.
 */
const growthLine = (
  points: AppDatabaseUsage['points'],
  used?: number,
  quota?: number
): string | null => {
  if (points.length < 2) {
    return null;
  }

  const first = points[0];
  const last = points[points.length - 1];
  const days = Math.max(
    1,
    Math.round(
      (new Date(last.time).getTime() - new Date(first.time).getTime()) / 86400000
    )
  );
  const delta = last.sizeBytes - first.sizeBytes;

  const span = `over ${days} day${days === 1 ? '' : 's'}`;

  if (delta <= 0) {
    // A shrink is worth its own words: it is what a VACUUM looks like, and
    // someone who has just deleted a lot of rows is checking for exactly it.
    return delta === 0
      ? `No change ${span}`
      : `Down ${formatBytes(-delta)} ${span}`;
  }

  const growth = `Up ${formatBytes(delta)} ${span}`;

  if (points.length < 7 || !quota || used === undefined) {
    return growth;
  }

  const headroom = quota - used;

  if (headroom <= 0) {
    return growth;
  }

  const daysLeft = Math.round(headroom / (delta / days));

  if (daysLeft > 730) {
    return `${growth} · years of room at this rate`;
  }

  if (daysLeft >= 60) {
    return `${growth} · full in about ${Math.round(daysLeft / 30)} months at this rate`;
  }

  return `${growth} · full in about ${daysLeft} days at this rate`;
};

const StorageUsage = ({ usage, isLoadingUsage }: {
  usage: AppDatabaseUsage,
  isLoadingUsage: boolean,
}) => {
  // No pack, no card. This sits on the Plan screen, where every other block
  // is about something the app is paying for — a storage bar for an app that
  // never bought the database reads as a limit it is subject to.
  if (!isLoadingUsage && usage.packs === 0) {
    return null;
  }

  const points = usage.points;
  const used = usage.sizeBytes ?? undefined;
  const quota = usage.quotaBytes || 0;
  const tone = TONE[usage.status] || TONE.OK;
  const ratio = used !== undefined && quota > 0 ? used / quota : 0;
  // Never past full: a fill wider than its own track is just a broken layout.
  // How far over the app actually is, is the percentage beside it.
  const barWidth = Math.min(Math.max(ratio, 0), 1) * 100;

  const peak = points.reduce((max, point) => Math.max(max, point.sizeBytes), 0);
  // Two points is the least that can show a direction. Below that the chart
  // block is not rendered at all rather than drawn empty.
  const hasChart = points.length >= 2;

  // A fixed window of days, not one slot per measurement.
  //
  // Two things come from this. The row always spans the card — bars sized only
  // to the points that exist left a chart that stopped halfway across and read
  // as clipped. And a day the sweep did not run is an empty slot in its own
  // place, which is what makes the gap visible instead of closing it up and
  // showing twenty-one days of history as if they were consecutive.
  const axis = (() => {
    const byDay = new Map(
      points.map(point => [
        new Date(point.time).toISOString().slice(0, 10),
        point.sizeBytes,
      ])
    );
    const today = new Date();
    const slots: { key: string, time: Date, sizeBytes?: number }[] = [];

    for (let back = DAYS - 1; back >= 0; back--) {
      const time = new Date(
        Date.UTC(
          today.getUTCFullYear(),
          today.getUTCMonth(),
          today.getUTCDate() - back
        )
      );
      const key = time.toISOString().slice(0, 10);

      slots.push({ key, time, sizeBytes: byDay.get(key) });
    }

    return slots;
  })();

  const growth = growthLine(points, used, quota);

  return (
    <SurfaceCard tone="surface" className="p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Eyebrow>storage</Eyebrow>
        <span className="font-sora text-ds-15 font-semibold text-white">
          {used === undefined ? 'Not measured yet' : formatBytes(used)}
          {quota > 0 && used !== undefined && (
            <span className="text-navy-300 font-normal"> of {formatBytes(quota)}</span>
          )}
        </span>
        {used !== undefined && quota > 0 && (
          <span className={`text-ds-115 ${tone.text}`}>
            {/* Never "0%" for a database that has something in it: a reading
                that rounds to nothing still rounds up to the smallest number
                that is true. */}
            {ratio > 0 && ratio < 0.01 ? '<1' : Math.round(ratio * 100)}% · {tone.label}
          </span>
        )}
        <span className="ml-auto text-ds-115 text-navy-300">
          {usage.sizeMeasuredAt
            ? measuredLabel(usage.sizeMeasuredAt)
            : 'not swept yet'}
        </span>
      </div>

      {quota > 0 ? (
        <div>
          {/* The empty track is navy-600, a step LIGHTER than the surface it
              sits on. It was navy-900, which on a navy-800 card is very nearly
              the same value — so at low usage there was no track to see and the
              fill read as a green dash floating in the card. */}
          <div className="h-1.5 w-full rounded-full bg-navy-600 overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${barWidth}%`,
                // A percentage alone collapses a real reading into a dot.
                minWidth: used && used > 0 ? '0.5rem' : 0,
              }}
            >
              <div className={`h-full w-full rounded-full ${tone.bar}`} />
            </div>
          </div>
          {/* The line the card was missing. A bar answers "how full", which
              for almost every database is "barely"; this answers "and where
              is it going", which is the question a quota is actually read
              for. */}
          {growth ? (
            <p className="mt-2 text-ds-125 text-navy-100">{growth}</p>
          ) : null}
          <p className="mt-2 text-ds-115 text-navy-300">
            {usage.packs} pack{usage.packs === 1 ? '' : 's'} ·{' '}
            {formatBytes(quota)} included. Measured as size on disk, so indexes
            and space freed by a delete count until the server reclaims it.
          </p>
        </div>
      ) : (
        <p className="text-ds-115 text-navy-300">
          This database has no storage allowance recorded, so there is nothing to
          measure against.
        </p>
      )}

      {isLoadingUsage ? (
        <div className="h-1.5 w-32 rounded bg-navy-900 animate-pulse" />
      ) : hasChart ? (
        <div className="pt-1">
          <div className="flex items-baseline justify-between text-ds-115 text-navy-300">
            <span>Last {DAYS} days</span>
            <span>peak {formatBytes(peak)}</span>
          </div>
          <div className="mt-1.5 flex items-end gap-[2px] h-16 w-full">
            {axis.map(slot => slot.sizeBytes === undefined ? (
              <div
                key={slot.key}
                title={`${dayLabel(slot.time)} · not measured`}
                className="flex-1 h-[2px] rounded-t bg-navy-600"
              />
            ) : (
              <div
                key={slot.key}
                title={`${dayLabel(slot.time)} · ${formatBytes(slot.sizeBytes)}`}
                className="flex-1 bg-action/70 hover:bg-action rounded-t transition-colors"
                style={{
                  // A measured day that rounds to nothing still gets height:
                  // an invisible bar and the flat tick above mean different
                  // things and must not look the same.
                  height: `${Math.max((slot.sizeBytes / (peak || 1)) * 100, 6)}%`,
                }}
              />
            ))}
          </div>
          <div className="mt-1.5 flex justify-between text-ds-115 text-navy-300">
            <span>{dayLabel(axis[0].time)}</span>
            <span>Today</span>
          </div>
        </div>
      ) : (
        <p className="text-ds-115 text-navy-300">
          {/* What is missing is time, not data, so the line says when it
              arrives rather than describing a chart that is not there. */}
          {points.length === 1
            ? 'Measured once. Growth and a chart appear after tomorrow\'s sweep.'
            : 'Measured once a day. The first reading lands on the next sweep.'}
        </p>
      )}
    </SurfaceCard>
  );
};

export default StorageUsage;

/**
 * The same reading as one strip, for the foot of the Plan screen's "running
 * now" card: what the packs allow, what is in use, a bar, and — quietly — the
 * day it was last checked. The chart stays on Metrics — on Plan the question is only "how
 * much of what I pay for am I using".
 */
export const StorageUsageStrip = ({ usage }: { usage: AppDatabaseUsage }) => {
  const used = usage.sizeBytes ?? undefined;
  const quota = usage.quotaBytes || 0;
  const tone = TONE[usage.status] || TONE.OK;
  const ratio = used !== undefined && quota > 0 ? used / quota : 0;
  const barWidth = Math.min(Math.max(ratio, 0), 1) * 100;

  return (
    <div className="flex flex-col gap-[9px] px-6 py-[18px] lg:px-[26px] border-t border-navy-700">
      <div className="flex items-baseline justify-between gap-3">
        <Eyebrow>database storage</Eyebrow>
        <span className="font-courrier text-ds-115 text-navy-300 whitespace-nowrap">
          {usage.packs} pack{usage.packs === 1 ? '' : 's'} included
        </span>
      </div>
      <div className="flex items-baseline gap-[7px]">
        <span className={`font-sora text-ds-20 font-semibold tracking-[-0.02em] ${used === undefined ? 'text-navy-300' : 'text-white'}`}>
          {used === undefined ? '—' : formatBytes(used)}
        </span>
        <span className="font-courrier text-ds-115 text-navy-300">of {formatBytes(quota)}</span>
        {used !== undefined && quota > 0 ? (
          <span className={`ml-1 text-ds-115 ${tone.text}`}>
            {ratio > 0 && ratio < 0.01 ? '<1' : Math.round(ratio * 100)}% · {tone.label}
          </span>
        ) : null}
      </div>
      <div className="h-1 w-full rounded-full bg-navy-600 overflow-hidden">
        {used !== undefined ? (
          <div
            className={`h-full rounded-full ${tone.bar}`}
            style={{ width: `${barWidth}%`, minWidth: used > 0 ? '0.5rem' : 0 }}
          />
        ) : null}
      </div>
      {/* Only when it was read: the number above is a daily reading, and the
          date is what keeps a stale one from passing as live. */}
      <span className="font-courrier text-ds-105 text-navy-300/80">
        {usage.sizeMeasuredAt
          ? `Last checked ${new Date(usage.sizeMeasuredAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
          : 'Not checked yet'}
      </span>
    </div>
  );
};
